import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { LOGO_URL } from "../lib/constants";

const inputClass =
  "w-full bg-transparent border-b border-white/20 focus:border-gold text-white placeholder-white/30 py-4 font-sans text-sm outline-none transition-colors duration-300";

export default function PrivateAccess() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        if (!form.name) throw { response: { data: { detail: "Please enter your name." } } };
        await register(form.name, form.email, form.password);
        toast.success("Your private account has been created.");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy grain relative flex items-center justify-center px-6 py-16 overflow-hidden">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: "url(https://images.pexels.com/photos/13203179/pexels-photo-13203179.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-navy/80" />

      <Link
        to="/"
        data-testid="back-home"
        className="absolute top-8 left-8 z-10 flex items-center gap-2 text-white/60 hover:text-gold transition-colors font-sans text-xs tracking-wide-lux uppercase"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md glass rounded-2xl p-8 md:p-12"
      >
        <div className="flex flex-col items-center text-center mb-10">
          <img src={LOGO_URL} alt="Royce International" className="h-24 w-auto object-contain mb-6" />
          <p className="font-sans text-gold text-[10px] tracking-luxury uppercase mb-3">Private Access</p>
          <h1 className="font-serif text-white text-4xl">{mode === "login" ? "Welcome Back" : "Join the Circle"}</h1>
          <p className="font-sans text-white/40 text-sm mt-3">
            {mode === "login" ? "Access your confidential dashboard." : "Create your private client account."}
          </p>
        </div>

        <form onSubmit={submit} data-testid="auth-form" className="space-y-6">
          {mode === "register" && (
            <input data-testid="auth-name" className={inputClass} placeholder="Full name" value={form.name} onChange={update("name")} />
          )}
          <input data-testid="auth-email" type="email" required className={inputClass} placeholder="Email address" value={form.email} onChange={update("email")} />
          <input data-testid="auth-password" type="password" required className={inputClass} placeholder="Password" value={form.password} onChange={update("password")} />

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit"
            className="w-full bg-gold text-navy py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {loading ? "Please wait…" : mode === "login" ? "Enter" : "Create Account"}
          </button>
        </form>

        <div className="mt-8 text-center">
          <button
            data-testid="auth-toggle"
            onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}
            className="font-sans text-white/50 text-sm hover:text-gold transition-colors"
          >
            {mode === "login" ? "No account? Request access" : "Already a client? Sign in"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
